/**
 * Strings for the exam's setup, its integrity incidents and rigor modes, and
 * «no sé» (S10: components/exam/SessionSetup.tsx, IntegrityParts.tsx,
 * pages/ExamSessionPage.tsx).
 *
 * Spread into the main catalog; `INTEGRITY_EN` is typed against
 * `INTEGRITY_ES`, so a missing English string fails to compile.
 *
 * The incident phrases (`exam.integrity.<kind>`) start in lower case: they
 * go after «Esta pregunta:» and are capitalized where they open a sentence.
 */

export const INTEGRITY_ES = {
  // ── Antes de empezar ─────────────────────────────────────────────────────
  'exam.setup.title': 'Antes de empezar',
  'exam.setup.questions': '{count} preguntas',
  'exam.setup.questionsOne': '1 pregunta',
  'exam.setup.format': 'Cómo respondes',
  'exam.setup.oral': 'Oral',
  'exam.setup.oralBody': 'Te lee cada pregunta y te escucha. Tu voz se transcribe en este dispositivo.',
  'exam.setup.written': 'Por escrito',
  'exam.setup.writtenBody': 'Con el teclado. El mismo examen, para cuando no puedes hablar o prefieres escribir.',
  'exam.setup.oralOff': 'El examen oral está apagado en Ajustes.',
  'exam.setup.oralOffAction': 'Activarlo',
  'exam.setup.oralUnsupported': 'Este navegador no puede escuchar en directo: el examen será por escrito.',
  'exam.setup.checking': 'Comprobando los modelos de voz…',
  'exam.setup.download': 'La primera vez se descargan unos {mb} MB ({parts}). Se quedan en este dispositivo y después funcionan sin conexión.',
  'exam.setup.download.stt': 'reconocimiento de voz, {mb} MB',
  'exam.setup.download.vad': 'detector de voz, {mb} MB',
  'exam.setup.download.tts': 'voz Supertonic, {mb} MB',
  'exam.setup.download.runtime': 'motor, {mb} MB',
  'exam.setup.metered': 'Parece una conexión con datos limitados: mejor con wifi.',
  'exam.setup.rigor': 'Rigor',
  'exam.setup.default': 'por defecto',
  'exam.setup.rigor.practice': 'Práctica',
  'exam.setup.rigor.practiceBody': 'Lo que pase se anota y se te enseña. No cambia nada más.',
  'exam.setup.rigor.exam': 'Examen',
  'exam.setup.rigor.examBody': 'Si pasa algo antes de responder, ese intento no cuenta para el dominio y suma deuda. Nunca te quita puntos.',
  'exam.setup.rigor.strict': 'Estricto',
  'exam.setup.rigor.strictBody': 'En pantalla completa. Si pasa algo antes de responder, la pregunta se anula y vuelve más tarde con otra variante.',
  'exam.setup.watch.title': 'Qué se vigila, y qué no',
  'exam.setup.watch.hidden': 'Salir de la página: cambiar de pestaña o de aplicación, o apagar la pantalla. Se anota el tiempo fuera.',
  'exam.setup.watch.blur': 'Tener otra ventana delante 2 s o más.',
  'exam.setup.watch.otherTab': 'Abrir o usar QUIPWA en otra pestaña.',
  'exam.setup.watch.paste': 'Pegar texto en tu respuesta.',
  'exam.setup.watch.copy': 'Copiar el enunciado.',
  'exam.setup.watch.mic': 'En el examen oral, un micrófono sin ninguna señal durante 8 s (silenciado).',
  'exam.setup.watch.fullscreen': 'En Estricto, salir de pantalla completa.',
  'exam.setup.watch.when': 'Cuenta lo que pase desde que ves la pregunta hasta que eliges tu confianza. Mientras corrige el modelo o lees la corrección puedes mirar otra cosa, salvo que luego toque otra ronda.',
  'exam.setup.watch.slips': 'Menos de 1 s fuera de la página no cuenta: es un toque sin querer.',
  'exam.setup.watch.pause': 'Pausar no es una incidencia. En Examen y Estricto, la pregunta en curso no espera: vuelve más tarde con otra variante, o, si ya fallaste una ronda, al volver verás la respuesta.',
  'exam.setup.watch.phone': 'En el móvil, la grabación se para con la pantalla apagada o la app en segundo plano: eso cuenta como salir de la página.',
  'exam.setup.watch.blind': 'Lo que un navegador no puede ver: un móvil o un libro al lado, otra persona en la sala. Es autoestudio: el objetivo es no engañarte, no vigilarte.',
  'exam.setup.watch.local': 'Nadie más lo ve, tampoco el modelo que corrige: se guarda con cada intento, en tus datos de QUIPWA (y en tu propio Drive, si sincronizas).',
  'exam.setup.otherTabs': 'Hay otra pestaña de QUIPWA abierta. Ciérrala antes de empezar: si la usas durante el examen, cuenta como incidencia.',
  'exam.setup.otherTabsMany': 'Hay {count} pestañas más de QUIPWA abiertas. Ciérralas antes de empezar: si usas alguna durante el examen, cuenta como incidencia.',
  'exam.setup.start': 'Empezar',
  'exam.setup.startDownload': 'Descargar y empezar',
  'exam.setup.downloading': 'Descargando los modelos de voz. El examen empieza en cuanto estén listos.',
  'exam.setup.downloadingButton': 'Descargando…',
  'exam.setup.downloadFailed': 'No se pudieron descargar los modelos de voz.',
  'exam.setup.writeInstead': 'Empezar por escrito',
  'exam.setup.micAsking': 'Permite el micrófono para empezar…',
  'exam.setup.micDenied': 'Sin micrófono no hay examen oral. Permítelo en el navegador, o elige «Por escrito».',
  'exam.setup.back': 'Volver',

  // ── Durante la sesión ────────────────────────────────────────────────────
  'exam.session.dontKnow': 'No lo sé',
  'exam.session.paused.bodyStrict': 'La pregunta queda oculta y el micrófono, apagado. Pausar no es una incidencia, pero la pregunta en curso no espera: vuelve más tarde con otra variante, o, si ya fallaste una ronda, al volver verás la respuesta.',
  'exam.integrity.line': 'Esta pregunta: {what}.',
  'exam.integrity.effect.practice': 'Solo se anota.',
  'exam.integrity.effect.before': 'Este intento ya no cuenta para el dominio.',
  'exam.integrity.effect.grading': 'Si toca otra ronda, ya no contará para el dominio.',
  'exam.integrity.effect.gradingStrict': 'Si toca otra ronda, la pregunta se anulará.',
  'exam.integrity.effect.after': 'Fue después de responder: no cambia nada.',
  'exam.integrity.hidden.one': 'saliste de la página 1 vez, {s} s',
  'exam.integrity.hidden': 'saliste de la página {count} veces, {s} s',
  'exam.integrity.blur.one': 'tuviste otra ventana delante 1 vez, {s} s',
  'exam.integrity.blur': 'tuviste otra ventana delante {count} veces, {s} s',
  'exam.integrity.other_tab.one': 'usaste otra pestaña de QUIPWA',
  'exam.integrity.other_tab': 'usaste otra pestaña de QUIPWA {count} veces',
  'exam.integrity.fullscreen_exit.one': 'saliste de pantalla completa',
  'exam.integrity.fullscreen_exit': 'saliste de pantalla completa {count} veces',
  'exam.integrity.paste.one': 'pegaste {chars} caracteres en la respuesta',
  'exam.integrity.paste': 'pegaste texto {count} veces ({chars} caracteres)',
  'exam.integrity.copy.one': 'copiaste el enunciado',
  'exam.integrity.copy': 'copiaste el enunciado {count} veces',
  'exam.integrity.mic_silent.one': 'el micrófono no dio señal durante {s} s',
  'exam.integrity.mic_silent': 'el micrófono se quedó sin señal {count} veces, {s} s',
  'exam.integrity.voided.title': 'Pregunta anulada',
  'exam.integrity.voided.cause': '{what}, antes de responder.',
  'exam.integrity.voided.requeued': 'Volverá más adelante en esta sesión, con otra variante.',
  'exam.integrity.voided.later': 'No queda otra variante en esta sesión: volverá en la próxima.',
  'exam.integrity.voided.cost': 'Cuenta como no respondida: suma deuda y no quita puntos.',
  'exam.integrity.fullscreen.title': 'Estás fuera de pantalla completa',
  'exam.integrity.fullscreen.back': 'Volver a pantalla completa',
  'exam.integrity.fullscreen.failed': 'Este navegador no deja usar pantalla completa aquí: el modo Estricto sigue sin ella.',
  'exam.integrity.tainted': 'Este intento no cuenta para el dominio: hubo una incidencia antes de responder. No te quita puntos.',

  // ── Cierre: el acta ──────────────────────────────────────────────────────
  'exam.summary.integrity': 'Incidencias',
  'exam.summary.integrityMode': 'Modo {mode}',
  'exam.summary.integrityNone': 'Sin incidencias.',
  'exam.summary.effect.voided': 'anuló la pregunta',
  'exam.summary.effect.tainted': 'antes de responder: el intento no contó',
  'exam.summary.effect.recorded': 'solo anotada',
  'exam.summary.effect.dropped': 'la pregunta quedó fuera de la sesión',
  'exam.summary.effect.after': 'después de responder: sin efecto',
  'exam.summary.outcome.void': 'Anulada',
  'exam.summary.dontKnow': 'no lo sabía',
  'exam.summary.tainted': 'no contó (incidencia)',
} as const

export const INTEGRITY_EN: Record<keyof typeof INTEGRITY_ES, string> = {
  'exam.setup.title': 'Before you start',
  'exam.setup.questions': '{count} questions',
  'exam.setup.questionsOne': '1 question',
  'exam.setup.format': 'How you answer',
  'exam.setup.oral': 'Out loud',
  'exam.setup.oralBody': 'Each question is read to you and you are listened to. Your voice is transcribed on this device.',
  'exam.setup.written': 'In writing',
  'exam.setup.writtenBody': 'With the keyboard. The same exam, for when you cannot speak or would rather write.',
  'exam.setup.oralOff': 'The oral exam is off in Settings.',
  'exam.setup.oralOffAction': 'Turn it on',
  'exam.setup.oralUnsupported': 'This browser cannot listen live: the exam will be in writing.',
  'exam.setup.checking': 'Checking the speech models…',
  'exam.setup.download': 'The first time, about {mb} MB are downloaded ({parts}). They stay on this device and then work offline.',
  'exam.setup.download.stt': 'speech recognition, {mb} MB',
  'exam.setup.download.vad': 'voice detector, {mb} MB',
  'exam.setup.download.tts': 'Supertonic voice, {mb} MB',
  'exam.setup.download.runtime': 'engine, {mb} MB',
  'exam.setup.metered': 'This looks like a metered connection: better on Wi-Fi.',
  'exam.setup.rigor': 'Rigor',
  'exam.setup.default': 'default',
  'exam.setup.rigor.practice': 'Practice',
  'exam.setup.rigor.practiceBody': 'Whatever happens is noted and shown to you. Nothing else changes.',
  'exam.setup.rigor.exam': 'Exam',
  'exam.setup.rigor.examBody': 'If something happens before you answer, that attempt does not count toward mastery and adds debt. It never takes points away.',
  'exam.setup.rigor.strict': 'Strict',
  'exam.setup.rigor.strictBody': 'Full screen. If something happens before you answer, the question is voided and comes back later as another variant.',
  'exam.setup.watch.title': 'What is watched, and what is not',
  'exam.setup.watch.hidden': 'Leaving the page: switching tab or app, or turning the screen off. The time away is noted.',
  'exam.setup.watch.blur': 'Having another window in front for 2 s or more.',
  'exam.setup.watch.otherTab': 'Opening or using QUIPWA in another tab.',
  'exam.setup.watch.paste': 'Pasting text into your answer.',
  'exam.setup.watch.copy': 'Copying the question.',
  'exam.setup.watch.mic': 'In the oral exam, a microphone with no signal at all for 8 s (muted).',
  'exam.setup.watch.fullscreen': 'In Strict, leaving full screen.',
  'exam.setup.watch.when': 'What counts is what happens from seeing the question to choosing your confidence. While the model grades or you read the feedback you may look elsewhere, unless another round follows.',
  'exam.setup.watch.slips': 'Less than 1 s off the page does not count: that is a slip.',
  'exam.setup.watch.pause': 'Pausing is not an incident. In Exam and Strict the question in progress does not wait: it comes back later as another variant, or, if you already failed a round, you see the answer when you resume.',
  'exam.setup.watch.phone': 'On a phone, recording stops with the screen off or the app in the background: that counts as leaving the page.',
  'exam.setup.watch.blind': 'What a browser cannot see: a phone or a book beside you, another person in the room. This is self-study: the point is not to fool yourself, not to watch you.',
  'exam.setup.watch.local': 'Nobody else sees it, not even the grading model: it is kept with each attempt, in your QUIPWA data (and in your own Drive, if you sync).',
  'exam.setup.otherTabs': 'Another QUIPWA tab is open. Close it before you start: using it during the exam counts as an incident.',
  'exam.setup.otherTabsMany': '{count} more QUIPWA tabs are open. Close them before you start: using one during the exam counts as an incident.',
  'exam.setup.start': 'Start',
  'exam.setup.startDownload': 'Download and start',
  'exam.setup.downloading': 'Downloading the speech models. The exam starts as soon as they are ready.',
  'exam.setup.downloadingButton': 'Downloading…',
  'exam.setup.downloadFailed': 'The speech models could not be downloaded.',
  'exam.setup.writeInstead': 'Start in writing',
  'exam.setup.micAsking': 'Allow the microphone to start…',
  'exam.setup.micDenied': 'There is no oral exam without a microphone. Allow it in the browser, or choose “In writing”.',
  'exam.setup.back': 'Back',

  'exam.session.dontKnow': 'I don’t know',
  'exam.session.paused.bodyStrict': 'The question stays hidden and the microphone off. Pausing is not an incident, but the question in progress does not wait: it comes back later as another variant, or, if you already failed a round, you see the answer when you resume.',
  'exam.integrity.line': 'This question: {what}.',
  'exam.integrity.effect.practice': 'Only noted.',
  'exam.integrity.effect.before': 'This attempt no longer counts toward mastery.',
  'exam.integrity.effect.grading': 'If another round follows, it will not count toward mastery.',
  'exam.integrity.effect.gradingStrict': 'If another round follows, the question will be voided.',
  'exam.integrity.effect.after': 'It was after you answered: nothing changes.',
  'exam.integrity.hidden.one': 'you left the page once, {s} s',
  'exam.integrity.hidden': 'you left the page {count} times, {s} s',
  'exam.integrity.blur.one': 'another window was in front once, {s} s',
  'exam.integrity.blur': 'another window was in front {count} times, {s} s',
  'exam.integrity.other_tab.one': 'you used another QUIPWA tab',
  'exam.integrity.other_tab': 'you used another QUIPWA tab {count} times',
  'exam.integrity.fullscreen_exit.one': 'you left full screen',
  'exam.integrity.fullscreen_exit': 'you left full screen {count} times',
  'exam.integrity.paste.one': 'you pasted {chars} characters into the answer',
  'exam.integrity.paste': 'you pasted text {count} times ({chars} characters)',
  'exam.integrity.copy.one': 'you copied the question',
  'exam.integrity.copy': 'you copied the question {count} times',
  'exam.integrity.mic_silent.one': 'the microphone gave no signal for {s} s',
  'exam.integrity.mic_silent': 'the microphone went silent {count} times, {s} s',
  'exam.integrity.voided.title': 'Question voided',
  'exam.integrity.voided.cause': '{what}, before answering.',
  'exam.integrity.voided.requeued': 'It comes back later in this session, as another variant.',
  'exam.integrity.voided.later': 'No other variant is left in this session: it comes back next time.',
  'exam.integrity.voided.cost': 'It counts as unanswered: it adds debt and takes no points away.',
  'exam.integrity.fullscreen.title': 'You are out of full screen',
  'exam.integrity.fullscreen.back': 'Back to full screen',
  'exam.integrity.fullscreen.failed': 'This browser does not allow full screen here: Strict mode goes on without it.',
  'exam.integrity.tainted': 'This attempt does not count toward mastery: there was an incident before you answered. It takes no points away.',

  'exam.summary.integrity': 'Incidents',
  'exam.summary.integrityMode': '{mode} mode',
  'exam.summary.integrityNone': 'No incidents.',
  'exam.summary.effect.voided': 'voided the question',
  'exam.summary.effect.tainted': 'before answering: the attempt did not count',
  'exam.summary.effect.recorded': 'only noted',
  'exam.summary.effect.dropped': 'the question left the session',
  'exam.summary.effect.after': 'after answering: no effect',
  'exam.summary.outcome.void': 'Voided',
  'exam.summary.dontKnow': 'did not know',
  'exam.summary.tainted': 'did not count (incident)',
}
